import React, { useEffect, useState, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, errMsg } from "../../lib/api";
import { Card, Btn, EmptyState } from "../../components/UI";
import { toast } from "sonner";
import { Loader2, RefreshCw, Newspaper, BarChart3, ExternalLink, Radio } from "lucide-react";

export const FeedsTab = ({ id }) => {
  const [feeds, setFeeds] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    api.get(`/political-intelligence/candidates/${id}/feeds`).then(r => setFeeds(r.data)).catch(() => setFeeds({}));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/feeds/sync`);
      setFeeds(r.data);
      const s = r.data.sync_result || {};
      toast.success(`Synced — ${s.generic_polls ?? 0} generic-ballot polls, ${s.candidate_polls ?? 0} favorability polls, ${s.news ?? 0} news items`);
      (s.errors || []).forEach(e => toast.warning(e));
    } catch (e) { toast.error(errMsg(e, "Feed sync failed")); }
    finally { setSyncing(false); }
  };

  if (!feeds) return <div className="p-8 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2 text-cyan-300" />Loading feeds…</div>;

  const generic = feeds.generic || {};
  const series = generic.series || [];
  const candPolls = feeds.candidate_polls || [];
  const news = feeds.news || [];
  const hasData = series.length > 0 || candPolls.length > 0 || news.length > 0;

  return (
    <div className="space-y-4" data-testid="feeds-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-500 max-w-xl">
          {feeds.note}
          {feeds.last_sync && <span className="ml-2 text-slate-600">· Last synced {feeds.last_sync.slice(0, 16).replace("T", " ")} UTC · auto-refreshes daily</span>}
        </div>
        <Btn onClick={sync} disabled={syncing} data-testid="sync-feeds-btn">
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync now
        </Btn>
      </div>

      {!hasData && !syncing && (
        <Card>
          <EmptyState icon={Radio} title="No live feed data yet"
            description="Sync to pull the latest public data — 2026 generic congressional ballot (VoteHub), candidate favorability polls when available, and live news for this race."
            action={<Btn onClick={sync} data-testid="empty-sync-feeds-btn"><RefreshCw className="w-4 h-4" /> Sync live feeds</Btn>} />
        </Card>
      )}

      {series.length > 0 && (
        <Card data-testid="generic-ballot-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-cyan-300" />
              <h3 className="font-display text-lg font-bold">2026 Generic Congressional Ballot</h3>
            </div>
            {generic.latest_avg && (
              <div className="flex gap-2">
                <span className="text-xs px-2.5 py-1 rounded-full border border-blue-400/30 bg-blue-400/10 text-blue-300" data-testid="generic-dem-avg">Dem {generic.latest_avg.dem}%</span>
                <span className="text-xs px-2.5 py-1 rounded-full border border-rose-400/30 bg-rose-400/10 text-rose-300" data-testid="generic-rep-avg">Rep {generic.latest_avg.rep}%</span>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-3">National environment indicator — average of the {generic.latest_avg?.n_polls ?? 0} most recent public polls. Source: {generic.source}.</p>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={series}>
                <CartesianGrid stroke="rgb(var(--contrast-rgb) / 0.06)" />
                <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 10 }} tickFormatter={v => (v || "").slice(5)} />
                <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} unit="%" domain={["dataMin - 3", "dataMax + 3"]} />
                <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.1)", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="dem" name="Democratic" stroke="var(--partisan-blue)" strokeWidth={1.8} dot={{ r: 1.5 }} connectNulls />
                <Line type="monotone" dataKey="rep" name="Republican" stroke="var(--partisan-red)" strokeWidth={1.8} dot={{ r: 1.5 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card data-testid="favorability-card">
          <div className="flex items-center gap-2 mb-1">
            <Radio className="w-4 h-4 text-emerald-300" />
            <h3 className="font-display text-lg font-bold">Public favorability tracking</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Favorability & approval polls matching this candidate on VoteHub — typically available for well-known figures.</p>
          {candPolls.length === 0 ? (
            <div className="text-sm text-slate-500 py-4">No public favorability polls found for this candidate name yet — most local and challenger races aren't tracked nationally.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {candPolls.map((p) => (
                <div key={p.external_id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`fav-poll-${p.external_id}`}>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-white font-medium">{p.pollster}</span>
                    <span className="text-slate-500">{p.date} · {p.poll_type}{p.sample_size ? ` · n=${p.sample_size}` : ""}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(p.answers || []).map((a, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-slate-300">{a.choice} {a.pct}%</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card data-testid="news-feed-card">
          <div className="flex items-center gap-2 mb-1">
            <Newspaper className="w-4 h-4 text-amber-300" />
            <h3 className="font-display text-lg font-bold">Race news feed</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Live headlines mentioning this candidate — via Google News.</p>
          {news.length === 0 ? (
            <div className="text-sm text-slate-500 py-4">No headlines yet — sync to pull the latest coverage.</div>
          ) : (
            <div className="space-y-2.5 max-h-72 overflow-y-auto">
              {news.map((n) => (
                <a key={n.key} href={n.link} target="_blank" rel="noreferrer" data-testid={`news-item-${n.key}`}
                  className="block group">
                  <div className="text-sm text-slate-200 group-hover:text-cyan-300 transition-colors leading-snug flex items-start gap-1.5">
                    <span className="flex-1">{n.title}</span>
                    <ExternalLink className="w-3 h-3 text-slate-600 shrink-0 mt-1 group-hover:text-cyan-300" />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{n.source}{n.published_at ? ` · ${n.published_at.slice(0, 10)}` : ""}</div>
                </a>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
